import {
  OpportunityTypeFilter,
  OpportunityType,
} from "../../../legacy/types/companyFilter";

export enum OpportunityTypeOption {
  ALL_COMPANIES = "ALL_COMPANIES",
  CUSTOMER = "CUSTOMER",
  WHITESPACE = "WHITESPACE",
  EXPANSION_WHITESPACE = "EXPANSION_WHITESPACE",
  PROSPECT_WHITESPACE = "PROSPECT_WHITESPACE",
}

export interface OpportunityTypeConfig {
  hasWhitespaceFile: boolean;
  enableHierarchyClassification: boolean;
  fileId?: string;
}

/**
 * Check if a specific opportunity type option should be disabled
 */
export const isOpportunityTypeOptionDisabled = (
  option: OpportunityTypeOption,
  config: OpportunityTypeConfig
): boolean => {
  // All Companies is always available
  if (option === OpportunityTypeOption.ALL_COMPANIES) {
    return false;
  }

  // If no whitespace file, all other options are disabled
  if (!config.hasWhitespaceFile) {
    return true;
  }

  // If whitespace file exists but hierarchy classification is disabled,
  // expansion and prospect whitespace options are disabled, and regular whitespace is shown instead
  if (!config.enableHierarchyClassification) {
    return (
      option === OpportunityTypeOption.EXPANSION_WHITESPACE ||
      option === OpportunityTypeOption.PROSPECT_WHITESPACE
    );
  }

  // If hierarchy classification is enabled, regular whitespace option is disabled
  // (replaced by expansion and prospect whitespace)
  if (
    config.enableHierarchyClassification &&
    option === OpportunityTypeOption.WHITESPACE
  ) {
    return true;
  }

  return false;
};

/**
 * Get tooltip message type for disabled opportunity type options
 */
export const getOpportunityTypeOptionTooltipType = (
  option: OpportunityTypeOption,
  config: OpportunityTypeConfig
): string | undefined => {
  if (!isOpportunityTypeOptionDisabled(option, config)) {
    return undefined;
  }

  // If no whitespace file, show specific message based on option
  if (!config.hasWhitespaceFile) {
    switch (option) {
      case OpportunityTypeOption.CUSTOMER:
        return "customer";
      case OpportunityTypeOption.WHITESPACE:
        return "whitespace";
      case OpportunityTypeOption.EXPANSION_WHITESPACE:
        return "expansion";
      case OpportunityTypeOption.PROSPECT_WHITESPACE:
        return "prospect";
    }
  }

  // If hierarchy classification is disabled, show specific message for expansion/prospect options
  if (!config.enableHierarchyClassification) {
    if (option === OpportunityTypeOption.EXPANSION_WHITESPACE) {
      return "expansion";
    }
    if (option === OpportunityTypeOption.PROSPECT_WHITESPACE) {
      return "prospect";
    }
  }

  return undefined;
};

/**
 * Map opportunity type option to OpportunityTypeFilter (new backend structure)
 */
export const mapOpportunityTypeToFilter = (
  option: OpportunityTypeOption,
  config: OpportunityTypeConfig
): OpportunityTypeFilter | undefined => {
  if (!config.fileId || option === OpportunityTypeOption.ALL_COMPANIES) {
    return undefined;
  }

  // Map OpportunityTypeOption to OpportunityType
  let mappedType: OpportunityType | undefined;
  switch (option) {
    case OpportunityTypeOption.CUSTOMER:
      mappedType = OpportunityType.CUSTOMER;
      break;
    case OpportunityTypeOption.WHITESPACE:
      mappedType = OpportunityType.WHITESPACE;
      break;
    case OpportunityTypeOption.EXPANSION_WHITESPACE:
      mappedType = OpportunityType.EXPANSION_WHITESPACE;
      break;
    case OpportunityTypeOption.PROSPECT_WHITESPACE:
      mappedType = OpportunityType.PROSPECT_WHITESPACE;
      break;
    default:
      return undefined;
  }

  return {
    type: mappedType,
  };
};

/**
 * Get the current opportunity type option from an OpportunityTypeFilter
 */
export const getOpportunityTypeFromFilter = (
  filter: OpportunityTypeFilter | undefined,
  config: OpportunityTypeConfig
): OpportunityTypeOption => {
  if (!filter || !config.fileId) {
    return OpportunityTypeOption.ALL_COMPANIES;
  }

  switch (filter.type) {
    case OpportunityType.CUSTOMER:
      return OpportunityTypeOption.CUSTOMER;
    case OpportunityType.WHITESPACE:
      return OpportunityTypeOption.WHITESPACE;
    case OpportunityType.EXPANSION_WHITESPACE:
      return OpportunityTypeOption.EXPANSION_WHITESPACE;
    case OpportunityType.PROSPECT_WHITESPACE:
      return OpportunityTypeOption.PROSPECT_WHITESPACE;
    default:
      return OpportunityTypeOption.ALL_COMPANIES;
  }
};

/**
 * Check if a current opportunity type filter is still valid given the current configuration
 */
export const isOpportunityTypeFilterValid = (
  filter: OpportunityTypeFilter | undefined,
  config: OpportunityTypeConfig
): boolean => {
  if (!filter) {
    return true; // No filter is always valid
  }

  // If no whitespace file, only "All Companies" is valid
  if (!config.hasWhitespaceFile) {
    return false; // Any actual filter is invalid without whitespace file
  }

  const currentOption = getOpportunityTypeFromFilter(filter, config);

  // Check if the current option is disabled under current configuration
  return !isOpportunityTypeOptionDisabled(currentOption, config);
};

/**
 * Get display label for opportunity type option
 */
export const getOpportunityTypeLabel = (
  option: OpportunityTypeOption
): string => {
  switch (option) {
    case OpportunityTypeOption.ALL_COMPANIES:
      return "All Companies";
    case OpportunityTypeOption.CUSTOMER:
      return "Customer";
    case OpportunityTypeOption.WHITESPACE:
      return "Whitespace";
    case OpportunityTypeOption.EXPANSION_WHITESPACE:
      return "Expansion Whitespace";
    case OpportunityTypeOption.PROSPECT_WHITESPACE:
      return "Prospect Whitespace";
    default:
      return "All Companies";
  }
};
